import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Card, Row, Col, Divider, Alert, Switch, Tooltip, Icon } from 'antd';
import { BulbOutlined } from '@ant-design/icons';

import {
  SEPARATOR_DASH,
  RATE_DISTRIBUTION_TAB,
  RATE_DERIVATIVE,
  WEEKDAY_RATE,
  WEEKEND_RATE,
  RATE_DERIVATIVES,
  MODIFIER_TYPE,
  MODIFIER_SIGN,
  MODIFIER_AMOUNT,
  PAX_THRESHOLD,
  EXTRA_PAX_FEE,
  AIRBNB_CANCELLATION_POLICIES
} from 'utils/constants';
import { generatePercentageFromDisplay, generateDisplayPercentage, calculateDerivedRate, checkHasValue } from 'utils/general';

import FormSelection from 'components/FormSelection/FormSelection';
import RateModifierFormItems from 'components/RateModifierFormItems/RateModifierFormItems';
import OccupancyRate from './components/OccupancyRate/OccupancyRate';
import RateFormInputNumber from './components/RateFormInputNumber/RateFormInputNumber';
import { FormInputReadOnly } from 'components/FormInput/FormInput';
import FormInputNumber from 'components/FormInputNumber/FormInputNumber';

import styles from './RateDistribution.module.css';
import intl from 'react-intl-universal';

class RateDistribution extends Component {
  static propTypes = {
    form: PropTypes.object.isRequired,
    roomId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    maxCapacity: PropTypes.number,
    otaId: PropTypes.string,
    rate: PropTypes.object.isRequired,
    isOccupancyRateApplicable: PropTypes.bool,
    rateModifierTypeConstants: PropTypes.array.isRequired,
    rateModifierSignConstants: PropTypes.array.isRequired,
    currency: PropTypes.string.isRequired,
    defaultBookingSetting: PropTypes.object
  };

  static defaultProps = {
    roomId: '',
    otaId: '',
    isOccupancyRateApplicable: false,
    defaultBookingSetting: {}
  };

  constructor(props) {
    super(props);
    const { roomId, otaId, rate, defaultBookingSetting } = props;
    const rateId = rate._id || '';
    const sep = SEPARATOR_DASH;

    this.state = {
      fieldNameRateDerivative: `${RATE_DISTRIBUTION_TAB}${sep}${roomId}${sep}${otaId}${sep}${rateId}${sep}${RATE_DERIVATIVE}`,
      fieldNameModifierType: `${RATE_DISTRIBUTION_TAB}${sep}${roomId}${sep}${otaId}${sep}${rateId}${sep}${MODIFIER_TYPE}`,
      fieldNameModifierSign: `${RATE_DISTRIBUTION_TAB}${sep}${roomId}${sep}${otaId}${sep}${rateId}${sep}${MODIFIER_SIGN}`,
      fieldNameModifierAmount: `${RATE_DISTRIBUTION_TAB}${sep}${roomId}${sep}${otaId}${sep}${rateId}${sep}${MODIFIER_AMOUNT}`,
      fieldNameWeekdayRate: `${RATE_DISTRIBUTION_TAB}${sep}${roomId}${sep}${otaId}${sep}${rateId}${sep}${WEEKDAY_RATE}`,
      fieldNameWeekendRate: `${RATE_DISTRIBUTION_TAB}${sep}${roomId}${sep}${otaId}${sep}${rateId}${sep}${WEEKEND_RATE}`,
      fieldNamePaxThreshold: `${RATE_DISTRIBUTION_TAB}${sep}${roomId}${sep}${otaId}${sep}${rateId}${sep}${PAX_THRESHOLD}`,
      fieldNameExtraPaxFee: `${RATE_DISTRIBUTION_TAB}${sep}${roomId}${sep}${otaId}${sep}${rateId}${sep}${EXTRA_PAX_FEE}`,
      hasOccupancyRate: false,
      defaultBookingSetting: defaultBookingSetting,
      priceFactor:
        defaultBookingSetting && Object.keys(defaultBookingSetting).length > 0 ? defaultBookingSetting.nonRefundablePriceFactor || 0.99 : 0.99,
      pricePreview: { weekday: 0.0, weekend: 0.0 },
      toggleNonRefundable:
        defaultBookingSetting && Object.keys(defaultBookingSetting).length > 0 ? !!defaultBookingSetting.nonRefundablePriceFactor : false
    };
  }

  componentDidMount = () => {
    const { rate, occupancyRate } = this.props;

    this.setState({ hasOccupancyRate: !!occupancyRate });
    this.setOccupancyRate(occupancyRate);
    this.setRate({ isDefaultToRateValue: true, isDerived: rate.isDerived });
  };

  setOccupancyRate = ({ noOfPaxThreshold, feePerExtraPax } = {}) => {
    const { form } = this.props;
    const { fieldNamePaxThreshold, fieldNameExtraPaxFee } = this.state;

    const payload = {};

    if (checkHasValue(noOfPaxThreshold)) {
      payload[fieldNamePaxThreshold] = noOfPaxThreshold;
    }
    if (checkHasValue(feePerExtraPax)) {
      payload[fieldNameExtraPaxFee] = feePerExtraPax;
    }

    form.setFieldsValue(payload);
  };

  setRate = ({ isDefaultToRateValue, isDerived, selectedType, selectedSign, selectedAmount }) => {
    const {
      fieldNameModifierType,
      fieldNameModifierSign,
      fieldNameModifierAmount,
      fieldNameWeekdayRate,
      fieldNameWeekendRate,
      priceFactor
    } = this.state;
    const { form, rate, rateModifierTypeConstants, rateModifierSignConstants } = this.props;

    const weekdayRate = rate.weekday;
    const weekendRate = rate.weekend;
    const rateType = rate.calculation.type;
    const rateIsPositive = rate.calculation.isPositive;
    const rateAmount = rate.calculation.amount;
    const parentWeekday = rate.calculation.parent.weekday;
    const parentWeekend = rate.calculation.parent.weekend;

    let {
      [fieldNameModifierType]: modifierType,
      [fieldNameModifierSign]: modifiersign,
      [fieldNameModifierAmount]: modifierAmount
    } = form.getFieldsValue([fieldNameModifierType, fieldNameModifierSign, fieldNameModifierAmount]);

    if (isDefaultToRateValue) {
      const isRatePercentage = !!rateModifierTypeConstants.find(type => type.code === rateType && type.isPercentage);

      modifierType = rateType;
      modifiersign = rateModifierSignConstants.find(sign => sign.isPositive === rateIsPositive).code;
      modifierAmount = isRatePercentage ? generateDisplayPercentage(rateAmount, false) : rateAmount;
    }

    const calcType = selectedType || modifierType || rateType;
    const isPercentage = !!rateModifierTypeConstants.find(type => type.code === calcType && type.isPercentage);
    const calcSign = selectedSign || modifiersign;
    const isPositive = !!calcSign ? !!rateModifierSignConstants.find(sign => sign.code === calcSign && sign.isPositive) : rateIsPositive;
    selectedAmount = !!selectedAmount || selectedAmount === 0 ? selectedAmount : modifierAmount;

    const calcAmount = isPercentage ? generatePercentageFromDisplay(selectedAmount) : selectedAmount;

    const { weekdayRate: resultWeekday, weekendRate: resultWeekend } = calculateDerivedRate({
      weekdayRate,
      weekendRate,
      isDerived,
      isPercentage,
      isPositive,
      calcAmount,
      parentWeekday,
      parentWeekend
    });

    form.setFieldsValue({
      [fieldNameWeekdayRate]: resultWeekday,
      [fieldNameWeekendRate]: resultWeekend
    });

    this.setState({ pricePreview: { weekday: weekdayRate * Number(priceFactor.toFixed(2)), weekend: weekendRate * Number(priceFactor.toFixed(2)) } });
  };

  handleOnRateDerivativeChange = selectedDerivative => {
    const isDerived = !!RATE_DERIVATIVES.find(derivative => derivative.code === selectedDerivative && derivative.isDerived);
    this.setRate({ isDefaultToRateValue: isDerived, isDerived });
  };

  handleOnTypeChange = selectedType => {
    this.setRate({ isDerived: true, selectedType });
  };

  handleOnSignChange = selectedSign => {
    this.setRate({ isDerived: true, selectedSign });
  };

  handleOnAmountChange = selectedAmount => {
    this.setRate({ isDerived: true, selectedAmount });
  };

  handleOnOccupancyRateChange = e => {
    this.setState({ hasOccupancyRate: e.target.value });
  };

  handleOnPricePreviewChange = priceFactor => {
    const { fieldNameWeekdayRate, fieldNameWeekendRate } = this.state;
    const { form } = this.props;
    const weekdayRate = form.getFieldValue(fieldNameWeekdayRate);
    const weekendRate = form.getFieldValue(fieldNameWeekendRate);
    this.setState({
      pricePreview: {
        weekday: weekdayRate * Number(((100 - priceFactor) / 100).toFixed(2)),
        weekend: weekendRate * Number(((100 - priceFactor) / 100).toFixed(2))
      },
      priceFactor
    });
  };

  handleToggleNonRefundable = e => {
    this.setState({ toggleNonRefundable: e });
  };

  render() {
    const {
      fieldNameRateDerivative,
      fieldNameModifierType,
      fieldNameModifierSign,
      fieldNameModifierAmount,
      fieldNameWeekdayRate,
      fieldNameWeekendRate,
      fieldNamePaxThreshold,
      fieldNameExtraPaxFee,
      hasOccupancyRate,
      defaultBookingSetting,
      pricePreview,
      priceFactor,
      toggleNonRefundable
    } = this.state;
    const {
      form,
      maxCapacity,
      rate,
      otaId,
      isOccupancyRateApplicable,
      rateModifierTypeConstants,
      rateModifierSignConstants,
      cancellationPolicyTab,
      currency
    } = this.props;
    const { [fieldNameRateDerivative]: selectedRateDerivative, [fieldNameModifierType]: selectedAdjustmentType } = form.getFieldsValue([
      fieldNameRateDerivative,
      fieldNameModifierType
    ]);

    const rateDerivative = RATE_DERIVATIVES.find(derivative =>
      !!selectedRateDerivative ? derivative.code === selectedRateDerivative : !!rate ? derivative.isDerived === rate.isDerived : derivative.isDefault
    );
    const isDerived = !!rateDerivative && rateDerivative.isDerived;
    const rateDerivativeCode = !!rateDerivative && rateDerivative.code;

    const selectedRateCalculation = (!!rate && rate.calculation) || undefined;

    const adjustmentTypeObject = rateModifierTypeConstants.find(
      type => type.code === (selectedAdjustmentType || (selectedRateCalculation && selectedRateCalculation.type)) || type.isDefault
    );

    const isPercentageType = !!adjustmentTypeObject && adjustmentTypeObject.isPercentage;
    const defaultAdjustmentType = !!adjustmentTypeObject ? adjustmentTypeObject.code : '';

    const adjustmentSignObject = rateModifierSignConstants.find(sign =>
      !!selectedRateCalculation ? sign.isPositive === selectedRateCalculation.isPositive : sign.isDefault
    );
    const defaultAdjustmentSign = !!adjustmentSignObject ? adjustmentSignObject.code : '';

    const defaultRateAdjustmentAmount = !!selectedRateCalculation ? selectedRateCalculation.amount : 0;

    const cancelationPolicy = defaultBookingSetting.cancellationPolicy;
    const weekdayPreviewFrom = Number(form.getFieldValue(fieldNameWeekdayRate)).toFixed(2);
    const weekdayPreviewTo = Number(pricePreview.weekday).toFixed(2);
    const weekendPreviewFrom = Number(form.getFieldValue(fieldNameWeekendRate)).toFixed(2);
    const weekendPreviewTo = Number(pricePreview.weekend).toFixed(2);

    return (
      <Card className={styles.card} title={`${rate.name ? `${rate.name} | ` : ''}${otaId}`} type="inner">
        <>
          {!cancellationPolicyTab && (
            <>
              <Row>
                <FormSelection
                  form={form}
                  name={fieldNameRateDerivative}
                  label={intl.get('hostConnect.integration.headerLabels.rateDerivative').d('Rate Derivative')}
                  placeholder={intl.get('hostConnect.integration.placeholder.rateDerivative').d('Please select a rate derivative')}
                  defaultValue={rateDerivativeCode}
                  selections={RATE_DERIVATIVES}
                  onSearch={() => {}}
                  onChange={this.handleOnRateDerivativeChange}
                />
              </Row>

              {isDerived && (
                <Row>
                  <RateModifierFormItems
                    form={form}
                    title={
                      <span className={styles.rateModifierFormItemsTitle}>
                        {intl.get('hostConnect.integration.headerLabels.rateAdjustment').d('Rate Adjustment')}{' '}
                      </span>
                    }
                    isPercentage={isPercentageType}
                    modifierTypeName={fieldNameModifierType}
                    defaultModifierType={defaultAdjustmentType}
                    modifierSignName={fieldNameModifierSign}
                    defaultModifierSign={defaultAdjustmentSign}
                    modifierAmountName={fieldNameModifierAmount}
                    defaultRateModifierAmount={defaultRateAdjustmentAmount}
                    minValue={0}
                    rateModifierTypeConstants={rateModifierTypeConstants}
                    rateModifierSignConstants={rateModifierSignConstants}
                    onTypeChange={this.handleOnTypeChange}
                    onSignChange={this.handleOnSignChange}
                    onAmountChange={this.handleOnAmountChange}
                  />
                </Row>
              )}

              <Row gutter={8}>
                <Col span={12}>
                  <RateFormInputNumber
                    form={form}
                    name={fieldNameWeekdayRate}
                    label={intl.get('hostConnect.integration.headerLabels.weekday').d('Weekday')}
                    isDisabled={isDerived}
                    currency={this.props.currency}
                  />
                </Col>

                <Col span={12}>
                  <RateFormInputNumber
                    form={form}
                    name={fieldNameWeekendRate}
                    label={intl.get('hostConnect.integration.headerLabels.weekend').d('Weekend')}
                    isDisabled={isDerived}
                    currency={this.props.currency}
                  />
                </Col>
              </Row>
            </>
          )}
          {cancellationPolicyTab && (
            <>
              <Row gutter={8} style={{ marginBottom: '8px' }}>
                <Col span={24}>
                  <Alert
                    message={intl.get('hostConnect.integration.rateDistribution.nonRefundablePricingTitle').d('Non-Refundable Pricing')}
                    description={
                      <p>
                        <BulbOutlined />
                        <span style={{ fontWeight: 600 }}>
                          {intl.get('hostConnect.integration.rateDistribution.nonRefundableHowItWorksBold').d('How it works:')}
                        </span>{' '}
                        <span>
                          {intl
                            .get('hostConnect.integration.rateDistribution.nonRefundableIntro')
                            .d(
                              'Set a discount percentage (1 - 99%) for your listing. Example: 30%, guest pays 70% (30% discount). Only available for Flexible, Moderate, Firm and Strict cancellation policies.'
                            )}
                        </span>{' '}
                        <span>
                          {intl
                            .get('hostConnect.integration.rateDistribution.nonRefundableAppliedToCalendar')
                            .d('The non-refundable pricing factor will be applied to your current Multi Calendar rate settings.')}
                        </span>
                      </p>
                    }
                    type="info"
                  />
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={4}>
                  <p className={styles.inputNumberLabel}>{intl.get('hostConnect.integration.rateDistribution.policyLabel').d('Policy')}</p>
                  <span>{AIRBNB_CANCELLATION_POLICIES.find(policy => policy.code === cancelationPolicy).label || cancelationPolicy}</span>
                </Col>
                <Col span={4}>
                  <p className={styles.inputNumberLabel}>
                    {intl.get('hostConnect.integration.rateDistribution.enableLabel').d('Enable')}{' '}
                    <Tooltip
                      title={intl
                        .get('hostConnect.integration.rateDistribution.enableNonRefundableTooltip')
                        .d(
                          'Non-refundable rates are only available for Flexible, Moderate, Firm, and Strict cancellation policies. This option is not available for Super Strict policies.'
                        )}
                    >
                      <Icon type="question-circle" />
                    </Tooltip>
                  </p>
                  {form.getFieldDecorator('toggleNonRefundable', {
                    valuePropName: 'checked',
                    initialValue: toggleNonRefundable
                  })(
                    <Switch
                      onChange={e => {
                        this.handleToggleNonRefundable(e);
                        this.handleOnPricePreviewChange(priceFactor);
                      }}
                      disabled={cancelationPolicy === 'super_strict_30' || cancelationPolicy === 'super_strict_60'}
                    />
                  )}
                </Col>
                <Col span={4}>
                  <>
                    {intl.get('hostConnect.integration.rateDistribution.discountLabel').d('Discount')}{' '}
                    <Tooltip
                      title={intl
                        .get('hostConnect.integration.rateDistribution.discountTooltip')
                        .d(
                          'Setting the discount rate for non-refundable pricing. Example: Setting 30% will mean that guests will receive a 30% discount on the listing price, and will be charged a non-refundable price which is 70% of the listing price.'
                        )}
                    >
                      <Icon type="question-circle" />
                    </Tooltip>
                  </>
                  <div style={{ display: 'flex' }}>
                    <FormInputNumber
                      form={form}
                      name="priceFactor"
                      defaultValue={priceFactor > 0 ? Number((1 - priceFactor) * 100).toFixed(0) : Number(1).toFixed(0)}
                      minValue={1}
                      maxValue={99}
                      onChange={this.handleOnPricePreviewChange}
                      disabled={!toggleNonRefundable}
                      parser={value => value && value.replace(/[^\d]/g, '')}
                      formatter={value => `${value}%`}
                      customStyle={{ width: '60%', alignItems: 'center' }}
                    />
                  </div>
                </Col>
                <Col span={6}>
                  <p className={styles.inputNumberLabel}>
                    {intl.get('hostConnect.integration.rateDistribution.weekdayPricePreview').d('Weekday Price Preview')}
                  </p>
                  {toggleNonRefundable && (
                    <span className={styles.pricePreview}>
                      {intl
                        .get('hostConnect.integration.rateDistribution.pricePreviewRange', {
                          currency,
                          from: weekdayPreviewFrom,
                          to: weekdayPreviewTo
                        })
                        .d(`${currency} ${weekdayPreviewFrom} → ${currency} ${weekdayPreviewTo}`)}
                    </span>
                  )}
                </Col>
                <Col span={6}>
                  <p className={styles.inputNumberLabel}>
                    {intl.get('hostConnect.integration.rateDistribution.weekendPricePreview').d('Weekend Price Preview')}
                  </p>
                  {toggleNonRefundable && (
                    <span className={styles.pricePreview}>
                      {intl
                        .get('hostConnect.integration.rateDistribution.pricePreviewRange', {
                          currency,
                          from: weekendPreviewFrom,
                          to: weekendPreviewTo
                        })
                        .d(`${currency} ${weekendPreviewFrom} → ${currency} ${weekendPreviewTo}`)}
                    </span>
                  )}
                </Col>
              </Row>
            </>
          )}

          {isOccupancyRateApplicable && (
            <>
              <Divider />

              <OccupancyRate
                form={form}
                hasOccupancyRate={hasOccupancyRate}
                onChange={this.handleOnOccupancyRateChange}
                maxCapacity={maxCapacity}
                fieldNames={{ fieldNameWeekdayRate, fieldNameWeekendRate, fieldNamePaxThreshold, fieldNameExtraPaxFee }}
                currency={this.props.currency}
              />
            </>
          )}
        </>
      </Card>
    );
  }
}

export default RateDistribution;
